import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import StatusBadge from '../../components/StatusBadge';
import API from '../../utils/api';
import { useState, useEffect } from 'react';
import { FileText, Clock, CheckCircle, PlusCircle, Megaphone, ChevronRight, ExternalLink } from 'lucide-react';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import ArticleStats from '../../components/ArticleStats';

export default function ReaderDashboard() {
  const { settings } = useSiteSettings();
  const sn = settings.siteName;
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ articles: [], totalViews: 0, avgViews: 0 });

  useEffect(() => {
    API.get('/articles/my-articles').then(r => { setArticles(r.data.articles || []); setLoading(false); }).catch(() => setLoading(false));
    API.get('/ads/my').then(r => setAds(r.data.ads || [])).catch(() => {});
    API.get('/articles/my-articles/stats').then(r => setStats({ articles: r.data.articles || [], totalViews: r.data.totalViews || 0, avgViews: r.data.avgViews || 0 })).catch(() => {});
  }, []);

  const pending = articles.filter(a => a.status === "payment_pending").length;
  const inProgress = articles.filter(a => !["payment_pending","published","rejected"].includes(a.status)).length;
  const needAction = articles.filter(a => a.status === "review_reader").length;
  const published = articles.filter(a => a.status === "published").length;

  return (
    <DashboardLayout title={"Selamat datang, " + (user?.name?.split(" ")[0] || "")} subtitle="Kelola artikel yang Anda ajukan">
      {needAction > 0 && (
        <div className="alert alert-warning" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{needAction} artikel menunggu persetujuan Anda</span>
          <Link to="/artikel-saya" className="btn btn-sm" style={{ background: "rgba(0,0,0,0.08)", color: "#000", border: "1px solid rgba(0,0,0,0.2)", padding: "5px 12px" }}>Lihat Sekarang</Link>
        </div>
      )}

      <div className="stats-row">
        {[
          { label: "Total Diajukan", val: articles.length, icon: <FileText size={20} />, color: "var(--in-blue)", bg: "rgba(0,21,64,0.08)" },
          { label: "Dalam Proses", val: inProgress, icon: <Clock size={20} />, color: "var(--in-blue)", bg: "rgba(0,21,64,0.08)" },
          { label: "Perlu Persetujuan", val: needAction, icon: <CheckCircle size={20} />, color: "var(--in-orange)", bg: "rgba(212,105,10,0.10)" },
          { label: "Terpublikasi", val: published, icon: <CheckCircle size={20} />, color: "var(--in-orange)", bg: "rgba(212,105,10,0.10)" },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-card-glow" style={{ background: s.bg }} />
            <div style={{ color: s.color }}>{s.icon}</div>
            <div className="stat-val">{s.val}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Widget iklan — sembunyikan jika fitur pasang iklan dinonaktifkan admin */}
      {settings.featureAdSubmission !== false && (
      <div className="ki-card" style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '18px 24px', marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{
          width: 44, height: 44, borderRadius: 10, flexShrink: 0,
          background: 'rgba(232,93,4,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Megaphone size={20} color="var(--in-orange)" />
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 3 }}>Pasang Iklan di {sn}</div>
          <div style={{ fontSize: 12, color: 'var(--ki-text-muted)', lineHeight: 1.5 }}>
            Jangkau pembaca aktif dari kalangan dosen, mahasiswa, dan profesional.
            {ads.length > 0 && <span style={{ marginLeft: 6, color: 'var(--ki-text-faint)' }}>{ads.length} pengajuan tercatat.</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {ads.length > 0 && (
            <Link to="/iklan-saya" className="btn btn-secondary btn-sm">
              Iklan Saya
            </Link>
          )}
          <Link to="/pasang-iklan" className="btn btn-primary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <PlusCircle size={14} /> Pasang Iklan <ChevronRight size={13} />
          </Link>
        </div>
      </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div className="section-title">Artikel Saya</div>
        <Link to="/publikasi" className="btn btn-primary btn-sm"><PlusCircle size={14} /> Ajukan Artikel</Link>
      </div>

      {loading ? (
        <div className="ki-card">{[...Array(4)].map((_, i) => <div key={i} className="shimmer" style={{ height: 52, borderRadius: 8, marginBottom: 10 }} />)}</div>
      ) : articles.length === 0 ? (
        <div className="ki-card" style={{ textAlign: "center", padding: "60px 24px" }}>
          <FileText size={40} style={{ color: "var(--ki-text-faint)", marginBottom: 12, margin: "0 auto 12px" }} />
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Belum ada artikel</div>
          <p style={{ color: "var(--ki-text-muted)", marginBottom: 20, fontSize: 14 }}>Mulai bagikan kisah inspiratif Anda hari ini.</p>
          <Link to="/publikasi" className="btn btn-primary">Ajukan Artikel Pertama</Link>
        </div>
      ) : (
        <div className="ki-card" style={{ padding: 0, overflow: "hidden" }}>
          <div className="ki-table-wrap">
          <table className="ki-table">
            <thead>
              <tr><th>Topik Artikel</th><th>Status</th><th>Diajukan</th><th>Aksi</th></tr>
            </thead>
            <tbody>
              {articles.map(a => (
                <tr key={a._id} style={{ background: a.status === "review_reader" ? "rgba(245,158,11,0.04)" : "" }}>
                  <td><div style={{ fontWeight: 600, maxWidth: 320, fontSize: 14 }}>{a.title || a.what}</div>{a.status === "review_reader" && <div style={{ fontSize: 11, color: "var(--in-orange)", marginTop: 2 }}>Menunggu persetujuan Anda</div>}</td>
                  <td><StatusBadge status={a.status} /></td>
                  <td style={{ fontSize: 13, color: "var(--ki-text-faint)" }}>{format(new Date(a.createdAt), "d MMM yyyy", { locale: id })}</td>
                  <td>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <Link to={"/artikel-saya/" + a._id + "/review"} className="btn btn-secondary btn-sm">Detail</Link>
                      {a.status === "published" && a.slug && (
                        <a href={"/berita/" + a.slug} target="_blank" rel="noopener noreferrer" className="btn btn-orange btn-sm" style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <ExternalLink size={13} /> Lihat di Portal
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
      <ArticleStats
        articles={stats.articles}
        totalViews={stats.totalViews}
        avgViews={stats.avgViews}
        label="Statistik Artikel Saya"
        secondaryCol="assignedEditor"
        secondaryLabel="Editor"
      />
    </DashboardLayout>
  );
}
